import { useEffect, useRef } from 'react';
import type { Phase } from '@ringmaster/shared';
import { stagePlayer } from './stagePlayer.js';
import { applyPhaseSoundscape, preloadUrls } from './soundscape.js';
import { getTuning } from './audioTuning.js';

/**
 * useNightAmbient — the Stage/host-device ambient soundscape, driven purely by
 * PublicView.phase changes. Runs ONLY on the host (the sole audio surface);
 * phones pass enabled=false and stay silent.
 *
 * The phase→sound mapping (which beds/layers loop, which stingers fire) lives in
 * soundscape.ts; ducking, stinger delays, and narration holds live in
 * tuning.json (see audioTuning.ts) and are applied by stagePlayer. This hook is
 * just the React glue: fire the soundscape once per real phase transition, warm
 * the decode cache on first enable, and silence everything on unmount.
 *
 * The TTS narration keeps playing over the ambient; stagePlayer ducks the bed +
 * one-shot buses while it speaks (levels in tuning.json).
 */
export function useNightAmbient(enabled: boolean, phase: Phase | null | undefined): void {
  // Track the previous phase so we only fire on transitions (not re-renders).
  const prevPhase = useRef<Phase | null>(null);
  // Warm the decode cache once (per enable) so stingers land on schedule.
  const preloaded = useRef(false);

  useEffect(() => {
    if (!enabled || !phase) return;

    // Warm all soundscape assets once. Safe no-op before unlock; if unlock
    // happens later, playOneShot's own decode still catches up.
    if (!preloaded.current) {
      preloaded.current = true;
      stagePlayer.preload(preloadUrls());
    }

    if (prevPhase.current === phase) return;
    prevPhase.current = phase;

    applyPhaseSoundscape(phase);
  }, [enabled, phase]);

  // On unmount (host leaves) — silence everything.
  useEffect(() => {
    return () => {
      if (enabled) stagePlayer.stopAmbient(getTuning().fades.unmountStopMs);
    };
  }, [enabled]);
}
